import { areObjectValuesEmpty } from 'aos-helpers/src/helpers/FormValidation'
import {
    translateParts,
    translatePartsOptional,
} from 'aos-helpers/src/helpers/translations/Translations'
import {
    EditableList,
    EditableListItemProps,
    EditableListValidation,
    HStack,
    Icon,
    PlusGlyph,
    Pressable,
    Text,
} from 'aos-ui-universal'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../../TaskFormField'

interface TaskEditableListFieldProps<T> {
    keyPrefix: string
    value: T[]
    emptyItem: T & { uuid: string }
    required?: boolean
    isError?: boolean
    validation?: EditableListValidation[]
    separated?: boolean
    maxItems?: number
    /** Seeds one blank row when the list is empty. */
    showFirstValue?: boolean
    seleniumLocation?: string
    className?: string
    itemRenderer(props: EditableListItemProps<T>): ReactNode
    onChange(value: T[]): void
}

/**
 * Universal replacement for the legacy `LabeledEditableList`: same `keyPrefix`
 * convention and the same add / seed / prune policy, rendered with the shared
 * form field and the universal `EditableList`.
 */
export function TaskEditableListField<T extends object>({
    keyPrefix,
    value,
    emptyItem,
    required,
    isError,
    validation,
    separated,
    maxItems,
    showFirstValue = true,
    seleniumLocation,
    className,
    itemRenderer,
    onChange,
}: TaskEditableListFieldProps<T>) {
    const { t } = useTranslation()

    // The legacy list pruned blank rows on every change; keep that, otherwise a
    // seeded empty row would be submitted as a real entry.
    const update = (next: T[]) => onChange(next.filter(item => !areObjectValuesEmpty(item)))

    const addItem = () =>
        update(
            value.length === 0 && showFirstValue
                ? [...value, emptyItem, emptyItem]
                : [...value, emptyItem],
        )

    const rows = value.length === 0 ? (showFirstValue ? [emptyItem] : []) : value
    const canAdd = maxItems === undefined || value.length !== maxItems

    return (
        <TaskFormField
            className={className}
            error={isError ? translatePartsOptional(t)(keyPrefix, 'error') : undefined}
            seleniumLocation={seleniumLocation}
        >
            <HStack className='items-center gap-0.5'>
                <Text size='body' tone='default' weight='medium' className='flex-1'>
                    {translateParts(t)(keyPrefix, 'label')}
                    {required ? ' *' : ''}
                </Text>
                {canAdd && (
                    <Pressable
                        accessibilityRole='button'
                        onPress={addItem}
                        className='h-8 w-8 items-center justify-center rounded hover:bg-aos-bg-hover-layer'
                    >
                        <Icon as={PlusGlyph} size={24} className='text-aos-text-muted' />
                    </Pressable>
                )}
            </HStack>
            <EditableList<T>
                value={rows}
                itemRenderer={itemRenderer}
                onChange={update}
                validation={validation}
                separated={separated}
            />
        </TaskFormField>
    )
}
